import React, { useEffect, useRef } from 'react';
import { useRipple } from '../context/RippleContext';

const RippleEffect = () => {
  const canvasRef = useRef(null);
  const ripplesRef = useRef([]);
  const animationRef = useRef(null);
  const tapCountRef = useRef(0);
  const { rippleEnabled } = useRipple();

  const liquidThemes = [
    // Iridescent
    {
      name: 'iridescent',
      getColor: (progress, angle) => {
        const hue = (progress * 360 + angle) % 360;
        return {
          primary: `hsla(${hue}, 80%, 70%, ${0.5 - progress * 0.45})`,
          highlight: `hsla(${(hue + 60) % 360}, 100%, 85%, ${0.4 - progress * 0.35})`,
        };
      }
    },
    // Chrome
    {
      name: 'chrome',
      getColor: (progress) => ({
        primary: `rgba(200, 200, 220, ${0.5 - progress * 0.45})`,
        highlight: `rgba(255, 255, 255, ${0.6 - progress * 0.55})`,
      })
    },
    // Money green
    {
      name: 'money',
      getColor: (progress) => ({
        primary: `rgba(34, 197, 94, ${0.5 - progress * 0.45})`,
        highlight: `rgba(134, 239, 172, ${0.6 - progress * 0.55})`,
      })
    },
    // Baby blue
    {
      name: 'babyblue',
      getColor: (progress) => ({
        primary: `rgba(147, 197, 253, ${0.5 - progress * 0.45})`,
        highlight: `rgba(219, 234, 254, ${0.6 - progress * 0.55})`,
      })
    },
    // Melting pink
    {
      name: 'pink',
      getColor: (progress) => ({
        primary: `rgba(244, 114, 182, ${0.5 - progress * 0.45})`,
        highlight: `rgba(251, 207, 232, ${0.6 - progress * 0.55})`,
      })
    }
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const createRipple = (x, y) => {
      // Don't create ripples if disabled (game is active)
      if (!rippleEnabled) return;
      
      const themeIndex = tapCountRef.current % 5;
      tapCountRef.current++;
      const theme = liquidThemes[themeIndex];

      const mainRipple = {
        x,
        y,
        startTime: Date.now(),
        duration: 6000,
        maxRadius: Math.max(canvas.width, canvas.height) * 0.5,
        theme,
        waves: [
          { delay: 0, speed: 1, amplitude: 1 },
          { delay: 200, speed: 0.92, amplitude: 0.7 },
          { delay: 400, speed: 0.84, amplitude: 0.4 },
        ]
      };

      ripplesRef.current.push(mainRipple);
    };

    const handleInteraction = (x, y) => {
      createRipple(x, y);
    };

    let lastTouchY = 0;
    let isScrolling = false;

    const handleClick = (e) => {
      if (!isScrolling && rippleEnabled) {
        handleInteraction(e.clientX, e.clientY);
      }
    };

    const handleTouchStart = (e) => {
      lastTouchY = e.touches[0].clientY;
      isScrolling = false;
    };

    const handleTouchMove = (e) => {
      const deltaY = Math.abs(e.touches[0].clientY - lastTouchY);
      if (deltaY > 10) {
        isScrolling = true;
      }
    };

    const handleTouchEnd = (e) => {
      if (!isScrolling && e.changedTouches[0] && rippleEnabled) {
        handleInteraction(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
      }
      isScrolling = false;
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    const easeOutSine = (t) => Math.sin((t * Math.PI) / 2);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const time = Date.now() / 1000;

      ripplesRef.current = ripplesRef.current.filter(ripple => {
        const elapsed = Date.now() - ripple.startTime;
        const progress = elapsed / ripple.duration;

        if (progress >= 1) return false;

        const { x, y, maxRadius, theme } = ripple;

        ripple.waves.forEach(wave => {
          const waveProgress = Math.max(0, progress - (wave.delay / ripple.duration));
          if (waveProgress <= 0 || waveProgress >= 1) return;

          const easedProgress = easeOutSine(waveProgress);
          const radius = easedProgress * maxRadius * wave.speed;
          const colors = theme.getColor(waveProgress, time * 30);

          if (radius < 1) return;

          const thickness = (1 - waveProgress) * 4 * wave.amplitude + 1;

          ctx.beginPath();
          const segments = 90;
          for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const wobble = Math.sin(angle * 3 + time * 1.5) * 2 * (1 - waveProgress);
            const r = radius + wobble;
            const px = x + Math.cos(angle) * r;
            const py = y + Math.sin(angle) * r;
            
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.strokeStyle = colors.primary;
          ctx.lineWidth = thickness;
          ctx.stroke();

          ctx.beginPath();
          for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const wobble = Math.sin(angle * 3 + time * 1.5) * 1.5 * (1 - waveProgress);
            const r = radius * 0.96 + wobble;
            const px = x + Math.cos(angle) * r;
            const py = y + Math.sin(angle) * r;
            
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.strokeStyle = colors.highlight;
          ctx.lineWidth = thickness * 0.5;
          ctx.stroke();
        });

        return true;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [rippleEnabled]);

  // Hide canvas when ripple is disabled
  if (!rippleEnabled) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[9998]"
      data-testid="ripple-canvas"
    />
  );
};

export default RippleEffect;
